import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addDays, parseIsoDate, toIsoDate, zonedCivilDate, zonedTimeToInstant } from '@/lib/timezone';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIRECTION,
  useJobListFilterPreferences,
  useSaveJobListFilterPreferences,
  type JobListFilterPreferences,
  type JobListFilters,
} from './useJobs';

/**
 * The list screen's filters, as the URL holds them — civil dates (`from`/
 * `to`), not the zoned instants the API filters on. Converting those needs
 * the account's timezone, which this module deliberately does not know about
 * (see `toApiFilters`) — the same separation Dispatch keeps between its own
 * URL state and `zonedTimeToInstant`.
 */
export interface JobListFilterState {
  status: string | null;
  assignedUserId: string | null;
  unassignedOnly: boolean;
  jobType: string;
  fromDate: string | null;
  toDate: string | null;
  includeUnscheduled: boolean;
  customerId: string | null;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

const PARAM = {
  status: 'status',
  assignedUserId: 'tech',
  unassignedOnly: 'unassigned',
  jobType: 'jobType',
  fromDate: 'from',
  toDate: 'to',
  includeUnscheduled: 'includeUnscheduled',
  customerId: 'customer',
  sortBy: 'sort',
  sortDirection: 'dir',
  page: 'page',
  pageSize: 'pageSize',
} as const;

/** Every key this screen owns on the URL — used only to detect "a completely fresh visit". */
const ALL_PARAM_KEYS = Object.values(PARAM);

const DEFAULTS: JobListFilterState = {
  status: null,
  assignedUserId: null,
  unassignedOnly: false,
  jobType: '',
  fromDate: null,
  toDate: null,
  includeUnscheduled: true,
  customerId: null,
  sortBy: DEFAULT_SORT_BY,
  sortDirection: DEFAULT_SORT_DIRECTION,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

function parseState(searchParams: URLSearchParams): JobListFilterState {
  const page = Number(searchParams.get(PARAM.page));
  const pageSize = Number(searchParams.get(PARAM.pageSize));
  return {
    status: searchParams.get(PARAM.status),
    assignedUserId: searchParams.get(PARAM.assignedUserId),
    unassignedOnly: searchParams.get(PARAM.unassignedOnly) === 'true',
    jobType: searchParams.get(PARAM.jobType) ?? '',
    fromDate: searchParams.get(PARAM.fromDate),
    toDate: searchParams.get(PARAM.toDate),
    // Absent means "on": an unfiltered list should show every job, scheduled
    // or not. The one way to turn it off is the explicit `=false`.
    includeUnscheduled: searchParams.get(PARAM.includeUnscheduled) !== 'false',
    customerId: searchParams.get(PARAM.customerId),
    sortBy: searchParams.get(PARAM.sortBy) ?? DEFAULT_SORT_BY,
    sortDirection: searchParams.get(PARAM.sortDirection) === 'desc' ? 'desc' : DEFAULT_SORT_DIRECTION,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize >= 1 ? pageSize : DEFAULT_PAGE_SIZE,
  };
}

function writeState(state: JobListFilterState): URLSearchParams {
  const next = new URLSearchParams();
  if (state.status) next.set(PARAM.status, state.status);
  if (state.assignedUserId) next.set(PARAM.assignedUserId, state.assignedUserId);
  if (state.unassignedOnly) next.set(PARAM.unassignedOnly, 'true');
  if (state.jobType.trim()) next.set(PARAM.jobType, state.jobType);
  if (state.fromDate) next.set(PARAM.fromDate, state.fromDate);
  if (state.toDate) next.set(PARAM.toDate, state.toDate);
  if (!state.includeUnscheduled) next.set(PARAM.includeUnscheduled, 'false');
  if (state.customerId) next.set(PARAM.customerId, state.customerId);
  if (state.sortBy !== DEFAULT_SORT_BY) next.set(PARAM.sortBy, state.sortBy);
  if (state.sortDirection !== DEFAULT_SORT_DIRECTION) next.set(PARAM.sortDirection, state.sortDirection);
  if (state.page !== 1) next.set(PARAM.page, String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) next.set(PARAM.pageSize, String(state.pageSize));
  return next;
}

/** The end of `toDate`'s calendar day, half-open — matches every other window filter in this API. */
function endOfDayInstant(isoDate: string, timeZone: string): Date {
  const date = parseIsoDate(isoDate);
  return date ? zonedTimeToInstant(addDays(date, 1), 0, 0, timeZone) : new Date(NaN);
}

/** `state`, translated into what `GET /jobs` actually filters on. */
export function toApiFilters(state: JobListFilterState, timeZone: string | undefined): JobListFilters {
  return {
    status: state.status,
    assignedUserId: state.assignedUserId,
    unassignedOnly: state.unassignedOnly,
    jobType: state.jobType,
    scheduledFrom:
      state.fromDate && timeZone
        ? zonedTimeToInstant(parseIsoDate(state.fromDate)!, 0, 0, timeZone).toISOString()
        : null,
    scheduledTo: state.toDate && timeZone ? endOfDayInstant(state.toDate, timeZone).toISOString() : null,
    includeUnscheduled: state.includeUnscheduled,
    customerId: state.customerId,
    sortBy: state.sortBy,
    sortDirection: state.sortDirection,
    page: state.page,
    pageSize: state.pageSize,
  };
}

function preferencesToState(preferences: JobListFilterPreferences, timeZone: string): JobListFilterState {
  return {
    status: preferences.status,
    assignedUserId: preferences.assignedUserId,
    unassignedOnly: preferences.unassignedOnly,
    jobType: preferences.jobType ?? '',
    fromDate: preferences.scheduledFrom ? toIsoDate(zonedCivilDate(new Date(preferences.scheduledFrom), timeZone)) : null,
    toDate: preferences.scheduledTo
      ? // Stored as the half-open upper bound (midnight of the day AFTER the
        // selected end date — see `endOfDayInstant`), so it is read back one
        // day earlier to recover the calendar date someone actually picked.
        toIsoDate(addDays(zonedCivilDate(new Date(preferences.scheduledTo), timeZone), -1))
      : null,
    includeUnscheduled: preferences.includeUnscheduled,
    customerId: preferences.customerId,
    sortBy: preferences.sortBy ?? DEFAULT_SORT_BY,
    sortDirection: preferences.sortDirection === 'desc' ? 'desc' : DEFAULT_SORT_DIRECTION,
    page: 1,
    // See the normalization note in Jobs.tsx: int32 fields come back typed as
    // `number | string` even though the API always sends a JSON number.
    pageSize: preferences.pageSize ? Number(preferences.pageSize) : DEFAULT_PAGE_SIZE,
  };
}

function stateToPreferences(state: JobListFilterState, timeZone: string | undefined): JobListFilterPreferences {
  const api = toApiFilters(state, timeZone);
  return {
    status: api.status,
    assignedUserId: api.assignedUserId,
    unassignedOnly: api.unassignedOnly,
    jobType: api.jobType.trim() || null,
    scheduledFrom: api.scheduledFrom,
    scheduledTo: api.scheduledTo,
    includeUnscheduled: api.includeUnscheduled,
    customerId: api.customerId,
    sortBy: api.sortBy,
    sortDirection: api.sortDirection,
    pageSize: api.pageSize,
  };
}

/**
 * The job list's filter state (M1-15): lives in the URL, like every other
 * filtered screen in this product, so a link someone sends opens to the same
 * list. "Saved/remembered per user" (the AC) layers on top of that rather
 * than replacing it: a completely fresh visit — no filter params on the URL
 * at all — is seeded from the signed-in user's last saved filters, and every
 * change after that both updates the URL and (debounced) re-saves.
 *
 * The two never fight: hydration only runs once, only when the URL is
 * entirely bare, and only after the saved preferences have actually loaded.
 */
export function useJobListFilters(timeZone: string | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = parseState(searchParams);

  const preferences = useJobListFilterPreferences();
  const savePreferences = useSaveJobListFilterPreferences();

  const hydrated = useRef(false);
  // The same fact as `hydrated`, as STATE, so the save effect below re-runs
  // when hydration settles even if the seeded filters equal the bare URL's.
  // ⚠️ Found by M2-19: with the account already cached (the header now reads
  // it, so it usually is), the timezone was there before the preferences,
  // hydration changed nothing the save effect depends on, and the skip meant
  // for hydration swallowed the person's FIRST real change instead.
  const [settled, setSettled] = useState(false);
  const isFreshVisit = ALL_PARAM_KEYS.every((key) => !searchParams.has(key));

  // Seed a bare URL from the saved filters, once, as soon as both the
  // timezone (needed to turn instants back into calendar dates) and the
  // saved preferences are available.
  useEffect(() => {
    if (hydrated.current || !isFreshVisit || !timeZone || !preferences.data) {
      return;
    }
    hydrated.current = true;
    setSettled(true);
    const seeded = preferencesToState(preferences.data, timeZone);
    setSearchParams(writeState(seeded), { replace: true });
    // Deliberately depends on only these two: `isFreshVisit`/`state` would
    // make this effect re-run on every filter change, but it must fire at
    // most once, guarded by `hydrated.current` above.
  }, [timeZone, preferences.data]);

  // A visit that already carries filter params (a shared link, Back) needs no
  // hydration, so it counts as settled immediately; nothing here should
  // trigger a save before the user has actually changed anything.
  useEffect(() => {
    if (!isFreshVisit) {
      hydrated.current = true;
      setSettled(true);
    }
    // Deliberately empty: runs once, at mount, against the URL as it was
    // when the screen opened.
  }, []);

  // Skips the save this effect would otherwise fire the first time it runs
  // with a real timezone — whether that is a fresh visit just hydrated from
  // the saved values (nothing to write back) or a shared link opened with
  // its own filters already on the URL (a one-time visit should not
  // overwrite what the user had saved before they clicked it). Only changes
  // made IN this screen, after that, are worth persisting.
  const skippedInitialSave = useRef(false);
  const filterKey = JSON.stringify({ ...state, page: undefined });
  useEffect(() => {
    if (!settled || !timeZone) {
      return;
    }
    if (!skippedInitialSave.current) {
      skippedInitialSave.current = true;
      return;
    }
    const timer = setTimeout(() => {
      savePreferences.mutate(stateToPreferences(state, timeZone));
    }, 600);
    return () => clearTimeout(timer);
    // `filterKey` is everything but `page` — turning pages must not spend a
    // save, and comparing the serialized filters (not `state` itself, a new
    // object every render) is what keeps this from re-arming the debounce
    // on every keystroke's own re-render.
  }, [filterKey, timeZone, settled]);

  function setState(patch: Partial<JobListFilterState>, opts: { resetPage?: boolean } = {}) {
    const next: JobListFilterState = {
      ...state,
      ...patch,
      page: opts.resetPage === false ? (patch.page ?? state.page) : 1,
    };
    setSearchParams(writeState(next));
  }

  return { state, setState };
}

export { DEFAULTS as DEFAULT_JOB_LIST_FILTER_STATE };
